import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { resetPassword } from '@/lib/api';

const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    if (pw !== pw2) {
      toast.error('Passwords do not match.');
      return;
    }
    if (!token) {
      toast.error('This reset link is missing its token.');
      return;
    }
    setLoading(true);
    try {
      await resetPassword(token, pw);
      setDone(true);
      toast.success('Password updated. You can sign in now.');
      setTimeout(() => nav('/login', { replace: true }), 1800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset failed. Try again or request a new link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-pd py-16 max-w-md" data-testid="reset-password-page">
      <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-[0.2em]">Password reset</div>
      <h1 className="font-display text-5xl leading-none mt-3">Choose a new password.</h1>

      {!token ? (
        <div className="mt-6 rounded-2xl border border-[color:var(--pd-danger)]/50 bg-[color:var(--pd-danger)]/10 p-5 text-sm" data-testid="reset-password-no-token">
          <div className="text-[color:var(--pd-text)] font-medium">Reset link incomplete.</div>
          <p className="text-muted-sage mt-1">The link you followed is missing its token. Please request a new reset link.</p>
          <Link to="/forgot-password" className="inline-block mt-3 text-[color:var(--pd-accent-yellow)] underline">
            Request a new reset link
          </Link>
        </div>
      ) : done ? (
        <div className="mt-6 rounded-2xl border border-[color:var(--pd-gold)]/40 bg-surface p-6" data-testid="reset-password-success">
          <div className="font-display text-2xl">Password updated.</div>
          <p className="text-sm text-muted-sage mt-2">You&rsquo;ll be redirected to sign in.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-3" data-testid="reset-password-form">
          <input
            required
            type="password"
            placeholder="New password (min 8 characters)"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className="w-full h-12 rounded-xl bg-surface border border-pd px-4 text-sm focus:border-[color:var(--pd-accent-yellow)] focus:outline-none"
            data-testid="reset-password-new"
          />
          <input
            required
            type="password"
            placeholder="Confirm new password"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            className="w-full h-12 rounded-xl bg-surface border border-pd px-4 text-sm focus:border-[color:var(--pd-accent-yellow)] focus:outline-none"
            data-testid="reset-password-confirm"
          />
          <button
            disabled={loading}
            className="w-full bg-[color:var(--pd-accent-yellow)] text-[#14251c] rounded-xl px-4 py-3.5 font-medium disabled:opacity-60"
            data-testid="reset-password-submit"
          >
            {loading ? 'Updating…' : 'Update password'}
          </button>
        </form>
      )}
    </div>
  );
};

export default ResetPasswordPage;
